import {
	Button,
	DropdownSelect,
	TextFieldWrapper,
	Modal,
	DialogContainer,
	ModalFooter,
	ModalHeading,
	ModalInputBox,
	ModalTitle,
	ModalTitleContainer,
	ModelSubHeading,
	Typography,
	Badge,
} from "@factorearth/component-library";
import Toggle from "@factorearth/component-library/dist/Atoms/Toggle/Toggle";
import { FormGroup, FormTemplateForResolver, Project, UserForResolver } from "db/definitions";
import React, { useEffect, useMemo, useState } from "react";
import { sortByAlphaNumericalArray } from "utils/sort";
import useCustomForm from "../../hooks/useCustomForm";
import { useTheme } from "../../hooks/useSingleton";
import buildFilterOptions from "../../utils/buildFilterOptions";
import filterTemplatesByGroupAndProject from "../../utils/filterTemplatesByGroupAndProject";
import styles from "./CreateFormGroupFolder.module.scss";
import { useLocation, useNavigate } from "react-router";
import { FiX, FiChevronRight, FiFileText, FiImage, FiChevronLeft, FiArchive, FiUsers } from "react-icons/fi";
import { GiPirateCaptain } from "react-icons/gi";

interface NewFormGroupData {
	name: string;
	templates: string[];
	type: "default" | "photolog";
}

interface OptionForSelect {
	label: string;
	value: string;
}

interface Props {
	closeCreateNew?: () => void;
	formGroups: FormGroup[];
	formTemplates?: FormTemplateForResolver[];
	getSingleFormGroup: (data: Pick<FormGroup, "id" | "name"> & Partial<FormGroup>) => Promise<void>;
	pageSize: number;
	project: Project | null;
	setPageIndex: React.Dispatch<React.SetStateAction<number>>;
	submitCreate?: (partialFormGroup: NewFormGroupData) => Promise<FormGroup | undefined>;
	user: UserForResolver;
	online: boolean;
}

function CreateFormGroupFolder(props: Props) {
	const { closeCreateNew, formGroups, formTemplates, getSingleFormGroup, pageSize, project, setPageIndex, submitCreate, user, online } = props;

	const color = useTheme();
	const navigate = useNavigate();
	const location = useLocation();

	const [buttonText, setButtonText] = useState("Submit");
	const [disabled, setDisabled] = useState<boolean>(true);
	const [disabledScreenOne, setDisabledScreenOne] = useState<boolean>(true);
	const [showUnpublishedTemplates, setShowUnpublishedTemplates] = useState<boolean>(false);
	const [templateGroupFilter, setTemplateGroupFilter] = useState("Show All Templates");
	const [groupFilter, setGroupFilter] = useState(true);
	const [projectFilter, setProjectFilter] = useState(false);
	const [templateTypeFilter, setTemplateTypeFilter] = useState({ value: "default", label: "default" });
	const [photolog, setPhotolog] = useState<boolean>(false);
	const [page, setPage] = useState<"createFolder" | "addTemplates">("createFolder");

	useEffect(() => {
		if (photolog && groupFilter) {
			setGroupFilter(false);
		}
	}, [photolog]);

	function handleGroupFilterToggle(nextState: React.SetStateAction<boolean>) {
		const enabled = typeof nextState === "function" ? nextState(groupFilter) : nextState;
		setGroupFilter(enabled);
		if (enabled) setProjectFilter(false);
	}

	function handleProjectFilterToggle(nextState: React.SetStateAction<boolean>) {
		const enabled = typeof nextState === "function" ? nextState(projectFilter) : nextState;
		setProjectFilter(enabled);
		if (enabled) setGroupFilter(false);
	}

	const initial: NewFormGroupData = {
		name: "",
		templates: [],
		type: "default",
	};

	const { values, handleChange, handleSubmit } = useCustomForm(initial, handleCreateNewFormGroup);

	useEffect(() => {
		if (values.name && values.templates && values.templates.length > 0) {
			setDisabled(false);
		} else {
			setDisabled(true);
		}
	}, [values.templates, values.name]);

	useEffect(() => {
		if (values.name) {
			setDisabledScreenOne(false);
		} else {
			setDisabledScreenOne(true);
		}
	}, [values.name]);

	function handleSubmitClick() {
		handleSubmit();
		let page = formGroups.length / pageSize;
		if (Number.isInteger(page) && page > 0) {
			setPageIndex(page + 1);
		}
	}

	async function handleCreateNewFormGroup(submittedValues: NewFormGroupData) {
		setDisabled(true);
		setButtonText("Creating...");
		const createdFormGroup = await submitCreate?.(submittedValues);
		if (createdFormGroup && user) {
			await getSingleFormGroup(createdFormGroup);
			navigate(`${location.pathname}/tabs/${createdFormGroup.id}`);
		}
		setDisabled(false);
		setButtonText("Submit");
		closeCreateNew?.();
	}

	const templateOptions = useMemo(() => {
		if (formTemplates && project) {
			let formTemplatesSorted = formTemplates.sort(sortTemplates);

			if (projectFilter || groupFilter) {
				formTemplatesSorted = filterTemplatesByGroupAndProject(formTemplatesSorted, groupFilter, projectFilter, user, project, true);
			}

			let grouped = formTemplatesSorted.reduce(
				(acc, template) => {
					const accTemplateGroup = acc[template.formTemplateGroup] || [];

					if (
						(templateTypeFilter.label === "photolog" && template.type === "photolog") ||
						(templateTypeFilter.label === "default" && template.type !== "photolog")
					) {
						return {
							...acc,
							[template.formTemplateGroup]: [...accTemplateGroup, template],
						};
					} else {
						return { ...acc };
					}
				},
				{} as Record<string, FormTemplateForResolver[]>,
			);

			if (templateGroupFilter !== "Show All Templates") {
				const key = templateGroupFilter;
				grouped = {
					[key]: grouped[templateGroupFilter],
				};
			}

			const keys = sortByAlphaNumericalArray({ array: Object.keys(grouped) });

			interface NestedOptions extends OptionForSelect {
				options?: OptionForSelect[];
			}

			const final: NestedOptions[] = [];

			for (let i = 0; i < keys.length; i++) {
				const groupName = keys[i];

				const groupOption: NestedOptions = {
					label: groupName,
					value: "",
					options: [],
				};

				const newOptions: { label: string; value: string }[] = [];

				const group = grouped[groupName];

				if (group) {
					for (let ii = 0; ii < group.length; ii++) {
						const groupedFormTemplate = group[ii];
						const { id, template, templateName, type, projectId, versionName, publish } = groupedFormTemplate;
						if (!publish && !showUnpublishedTemplates) {
							continue;
						}

						if (projectId && projectId !== project.id) {
							continue;
						}

						let label = templateName || template;
						if (versionName) {
							label = label + " - " + versionName;
						}

						const option = {
							label: label,
							value: id,
						};
						newOptions.push(option);
					}

					groupOption.options = newOptions;
					final.push(groupOption);
				}
			}
			return final;
		}
		return [];
	}, [
		formTemplates,
		templateTypeFilter,
		templateGroupFilter,
		showUnpublishedTemplates,
		groupFilter,
		projectFilter,
		project?.id,
		project?.syncFormTemplates,
		user.group,
	]);

	useEffect(() => {
		let option = "";
		if (photolog) {
			option = "photolog";
		} else {
			option = "default";
		}

		const typeOption = {
			target: {
				value: option,
				name: "type" as keyof NewFormGroupData,
			},
		};
		handleChange(typeOption);
		setTemplateTypeFilter({ value: option, label: option });
	}, [photolog]);

	function sortTemplates(a: FormTemplateForResolver, b: FormTemplateForResolver) {
		if (a.template < b.template) {
			return -1;
		}
		if (a.template > b.template) {
			return 1;
		}

		return 0;
	}

	function handleGroupFilter(templateGroupOption: unknown) {
		const newTemplateGroupOption = templateGroupOption as { value: string; label: string };
		const { value } = newTemplateGroupOption;
		setTemplateGroupFilter(value);
	}

	function selectHandleChangeTemplate(data: OptionForSelect) {
		if (photolog) {
			// Only allow one template for photolog
			handleChange({
				target: {
					value: [data.value],
					name: "templates" as keyof NewFormGroupData,
				},
			});
		} else {
			const currentTemplates = values.templates || [];
			handleChange({
				target: {
					value: [...currentTemplates, data.value],
					name: "templates" as keyof NewFormGroupData,
				},
			});
		}
	}

	const filterOption = ({ label, value }: OptionForSelect, string: string) => {
		const lowercaseString = string.toLowerCase();
		const lowercaseLabel = label.toLowerCase();
		const lowercaseValue = value.toLowerCase();
		if (lowercaseLabel.includes(lowercaseString) || lowercaseValue.includes(lowercaseString)) return true;

		const groupOptions = templateOptions.filter((group) => group.label.toLowerCase().includes(lowercaseString) && group.label !== "Select a template");

		if (groupOptions) {
			for (const groupOption of groupOptions) {
				const option = groupOption.options?.find((opt) => opt.value === lowercaseValue);
				if (option) return true;
			}
		}
		return false;
	};

	const buildCreateFolderPage = () => {
		return (
			<DialogContainer>
				<ModalTitleContainer>
					<ModalTitle>Create New Folder</ModalTitle>
					<FiX onClick={closeCreateNew} size={24} />
				</ModalTitleContainer>
				<ModelSubHeading>All fields are required unless otherwise noted</ModelSubHeading>
				<ModalInputBox style={{ width: "80%" }}>
					<ModalHeading>Folder Name</ModalHeading>
					<TextFieldWrapper
						id="formgroupfolder-name"
						data-test="form-group-create"
						name={"name" as keyof NewFormGroupData}
						type="text"
						value={values.name}
						onChange={(e) => {
							handleChange({
								target: {
									name: e.target.name as keyof NewFormGroupData,
									value: e.target.value,
									type: e.target.type,
									files: e.target.files,
								},
							});
						}}
						placeholder="Type name here"
						colorPalette={color}
						tabIndex={-1}
					/>
					<div className={styles.photologToggle}>
						<ModalHeading>Is this a Photo Log?</ModalHeading>
						<Toggle
							colorPalette={color}
							modes={[
								{
									mode: "No",
									icon: <FiFileText />,
								},
								{
									mode: "Yes",
									icon: <FiImage />,
								},
							]}
							setState={setPhotolog}
							state={photolog}
							data-test="type-toggle"
						/>
					</div>
				</ModalInputBox>
				<ModalFooter>
					<Button
						colorPalette={color}
						disabled={disabledScreenOne}
						onClick={() => {
							setPage("addTemplates");
						}}
						data-test="select-form-templates-btn"
						variant="filled"
						label="Select Form Templates"
						icon={<FiChevronRight />}></Button>
				</ModalFooter>
			</DialogContainer>
		);
	};

	const removeSelectedTemplate = (i: number) => {
		const currentTemplates = values.templates || [];
		let newTemplates: string[];
		if (photolog) {
			// Only allow 0 or 1
			newTemplates = [];
		} else {
			newTemplates = currentTemplates.filter((_, idx) => idx !== i);
		}
		handleChange({
			target: {
				value: newTemplates,
				name: "templates" as keyof NewFormGroupData,
			},
		});
	};

	// Helper function to get selected templates with labels
	const getSelectedTemplatesWithLabels = (): OptionForSelect[] => {
		if (!values.templates?.length || !formTemplates) {
			return [];
		}

		return values.templates.map((templateId) => {
			const template = formTemplates.find((t) => t.id === templateId);
			if (!template) return { value: templateId, label: templateId };

			let label = template.templateName || template.template;
			if (template.versionName) {
				label = label + " - " + template.versionName;
			}

			return { value: templateId, label };
		});
	};

	const buildListofSelectedTemplates = () => {
		const selectedTemplates = getSelectedTemplatesWithLabels();
		return (
			<>
				<Typography textColor={color.text.primary} variant={"Heading 5"}>
					Templates Selected:
				</Typography>
				<div className={styles.selectedTemplatesList}>
					{selectedTemplates.map((temp: OptionForSelect, i: number) => {
						return <Badge label={temp.label} theme={color} variant={"outlined"} key={i} icon={<FiX onClick={() => removeSelectedTemplate(i)} />} />;
					})}
				</div>
			</>
		);
	};

	const buildAddTemplatesPage = () => {
		return (
			<DialogContainer className={styles.folderCreationModal} id="modal-dialog-container">
				<ModalTitleContainer>
					<ModalTitle>Select Templates</ModalTitle>
					<FiX onClick={closeCreateNew} size={24} />
				</ModalTitleContainer>
				<ModelSubHeading>Choose the templates to add to this folder, you can always add more templates later.</ModelSubHeading>
				{!online && (
					<ModelSubHeading>
						You are <strong>offline</strong>. You can only add templates synced to your device. Return to online to view and add more templates.
					</ModelSubHeading>
				)}
				<ModalInputBox
					style={{
						backgroundColor: color.background.tertiary,
						width: "100%",
						padding: "24px",
						alignItems: "center",
						justifyContent: "center",
						position: "relative",
					}}
					id="modal-input-box">
					<div className={styles.filters}>
						{user.isDeveloper && (
							<div className={styles.toggleAndLabel}>
								<Typography variant={"Paragraphy Body"} textColor={color.text.primary} className={styles.label}>
									<GiPirateCaptain /> <strong>Unpublished</strong> templates
								</Typography>
								<Toggle
									colorPalette={color}
									modes={[{ mode: "" }, { mode: "" }]}
									setState={setShowUnpublishedTemplates}
									state={showUnpublishedTemplates}
									style={{ border: "none" }}
								/>
							</div>
						)}
						<div className={styles.toggleAndLabel}>
							<Typography variant={"Paragraphy Body"} textColor={color.text.primary} className={styles.label}>
								<FiUsers /> <strong>Group</strong> templates only
							</Typography>
							<Toggle
								key={`group-filter-${groupFilter}`}
								colorPalette={color}
								modes={[{ mode: "" }, { mode: "" }]}
								setState={handleGroupFilterToggle}
								state={groupFilter}
								style={{ border: "none" }}
								id="group-filter-checkbox"
								data-test="group-filter-toggle"
							/>
						</div>
						<div className={styles.toggleAndLabel}>
							<Typography variant={"Paragraphy Body"} textColor={color.text.primary} className={styles.label}>
								<FiArchive /> <strong>Current project</strong> templates only
							</Typography>
							<Toggle
								key={`project-filter-${projectFilter}`}
								colorPalette={color}
								modes={[{ mode: "" }, { mode: "" }]}
								setState={handleProjectFilterToggle}
								state={projectFilter}
								style={{ border: "none" }}
								id="project-filter-checkbox"
							/>
						</div>
					</div>

					{formTemplates && formTemplates.length > 0 ? (
						<>
							<DropdownSelect
								name="filter"
								id="filter"
								data-test="form-group-type"
								//@ts-ignore
								onChange={handleGroupFilter}
								value={{ label: templateGroupFilter, value: templateGroupFilter }}
								options={buildFilterOptions(formTemplates)}
								isSearchable={true}
								filterOption={filterOption}
								placeholder={"Filter by template group..."}
								//@ts-ignore
								colorPalette={color}
								maxMenuHeight={200}
							/>
							<DropdownSelect
								name="template"
								id="select-template"
								data-test="select-template"
								//@ts-ignore
								onChange={selectHandleChangeTemplate}
								value={null}
								options={templateOptions}
								isSearchable={true}
								filterOption={filterOption}
								//@ts-ignore
								colorPalette={color}
								maxMenuHeight={190}
							/>
						</>
					) : (
						<div style={{ color: color.text.secondary }}>One moment... Loading templates</div>
					)}
					<div className={styles.selectedTemplatesListContainer}>
						{(values.templates?.length || 0) > 0 ? (
							buildListofSelectedTemplates()
						) : (
							<Typography textColor={color.text.primary} variant={"Paragraphy Body"}>
								Select templates to add to the folder
							</Typography>
						)}
					</div>
				</ModalInputBox>
				<ModelSubHeading>If you need a template not listed here, please contact us at devs@factorearth.com</ModelSubHeading>
				<ModalFooter>
					<Button
						colorPalette={color}
						variant="outlined"
						label={"Previous"}
						onClick={() => {
							setPage("createFolder");
						}}
						icon={<FiChevronLeft />}
					/>
					<Button colorPalette={color} disabled={disabled} onClick={handleSubmitClick} data-test="create-form-group-button" variant="filled">
						{buttonText}
					</Button>
				</ModalFooter>
			</DialogContainer>
		);
	};

	return (
		<Modal colorPalette={color} open setOpen={() => {}} className={styles.modalContainer} id="modal">
			{page === "addTemplates" ? buildAddTemplatesPage() : buildCreateFolderPage()}
		</Modal>
	);
}

export default CreateFormGroupFolder;
